import React, {memo, useCallback, useEffect, useRef, useState} from "react";
import Picture from "./common/Picture";
import {ArrowRightIcon} from "./common/Icons";

interface GridItem {
    title: string;
    thumbnail: string;
    images: string[];
}

interface InteractiveGridCarouselProps {
    id: string;
    title: string;
    description: string;
    items: GridItem[];
}

export const InteractiveGridCarousel: React.FC<InteractiveGridCarouselProps> = memo(({
                                                                                         id,
                                                                                         title,
                                                                                         description,
                                                                                         items
                                                                                     }) => {
        const [itemOrder, setItemOrder] = useState(items.map((_, index) => index));
        const [activeSlideIndex, setActiveSlideIndex] = useState(0);
        const [isUserInteracting, setIsUserInteracting] = useState(false);
        const timeoutRef = useRef<NodeJS.Timeout | null>(null);
        const DURATION = 5000;

        const handleShuffleNext = useCallback(() => {
            setItemOrder(([main, top, bottom]) => [top, bottom, main]);
            setActiveSlideIndex(0);
        }, []);

        const handleShufflePrev = useCallback(() => {
            setItemOrder(([main, top, bottom]) => [bottom, main, top]);
            setActiveSlideIndex(0);
        }, []);

        const previousStory = useCallback(() => {
            setIsUserInteracting(true)
            if (activeSlideIndex > 0) {
                setActiveSlideIndex(prev => prev - 1);
            } else {
                handleShufflePrev();
            }

            setTimeout(() => setIsUserInteracting(false), 1000);
        }, [activeSlideIndex, handleShufflePrev]);

        const advanceStory = useCallback(() => {
            setIsUserInteracting(true);
            const mainItemIndex = itemOrder[0];
            const mainItem = items[mainItemIndex];

            if (activeSlideIndex >= mainItem.images.length - 1) {
                handleShuffleNext();
            } else {
                setActiveSlideIndex(prev => prev + 1);
            }

            setTimeout(() => setIsUserInteracting(false), 1000);
        }, [activeSlideIndex, itemOrder, items, handleShuffleNext]);

        // Auto advance functionality
        useEffect(() => {
            if (isUserInteracting) return;

            const resetTimeout = () => {
                if (timeoutRef.current) clearTimeout(timeoutRef.current);
            };

            resetTimeout();
            timeoutRef.current = setTimeout(advanceStory, DURATION);

            return () => resetTimeout();
        }, [advanceStory, isUserInteracting]);

        const getCardClass = (originalIndex: number) => {
            const position = itemOrder.indexOf(originalIndex);
            if (position === 0) return 'card-position-main';
            if (position === 1) return 'card-position-top';
            if (position === 2) return 'card-position-bottom';
            return '';
        };

        const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement, Event>, fallbackText: string) => {
            const target = e.currentTarget;
            target.onerror = null;
            target.src = `https://placehold.co/800x600/153D40/C6A375?text=${encodeURIComponent(fallbackText)}`;
        }, []);

        return (
            <section id={id} className="snap-section bg-ivory text-royal-pine overflow-hidden">
                <div className="px-4 sm:px-6 lg:px-8 h-full flex flex-col py-6 sm:py-8">
                    {/* Header */}
                    <div className="text-center mb-8 sm:mb-12">
                        <h2 className="heading-font text-3xl sm:text-4xl lg:text-5xl xl:text-6xl mb-4 leading-tight">
                            {title}
                        </h2>
                        <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-3xl mx-auto px-4">
                            {description}
                        </p>
                    </div>

                    {/* Grid Container */}
                    <div className="relative flex-1 min-h-0 max-w-7xl mx-auto w-full">
                        {/* Mobile Layout */}
                        <div className="block md:hidden space-y-4">
                            {items.map((item, index) => {
                                const position = itemOrder.indexOf(index);
                                const isMain = position === 0;

                                if (!isMain) return null;

                                return (
                                    <div
                                        key={item.title}
                                        className="relative w-full h-64 sm:h-80 md:h-full rounded-lg overflow-hidden shadow-lg">
                                        {item.images.map((image, imgIndex) => (
                                            <Picture
                                                key={`${item.title}-${imgIndex}`}
                                                {...(image.toLowerCase().endsWith('.webp') ? { webpSrc: image } : image.toLowerCase().endsWith('.png') ? { pngSrc: image } : { jpgSrc: image })}
                                                alt={`${item.title} slide ${imgIndex + 1}`}
                                                loading={imgIndex === 0 ? "eager" : "lazy"}
                                                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                                                    imgIndex === activeSlideIndex ? 'opacity-100' : 'opacity-0'
                                                }`}
                                                onErrorSrc={`https://placehold.co/800x600/153D40/C6A375?text=${encodeURIComponent(item.title)}`}
                                            />
                                        ))}

                                        {/* Mobile overlay */}
                                        <div className="absolute inset-0 bg-black bg-opacity-40"/>

                                        {/* Mobile progress indicators */}
                                        <div className="absolute top-4 left-4 right-4">
                                            <div className="flex space-x-2">
                                                {item.images.map((_, imgIndex) => (
                                                    <div key={imgIndex}
                                                         className="flex-1 h-1 bg-white bg-opacity-30 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full bg-white transition-all ${
                                                                imgIndex === activeSlideIndex ? 'story-progress' : ''
                                                            } ${imgIndex < activeSlideIndex ? 'w-full' : 'w-0'}`}
                                                            style={{animationDuration: `${DURATION}ms`}}
                                                        >

                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Mobile interactive areas */}
                                        <div className="absolute inset-0 flex">
                                            <button
                                                className="w-1/2 h-full focus:outline-none focus:bg-black focus:bg-opacity-10"
                                                onClick={previousStory}
                                                aria-label="Previous Slide"
                                            />
                                            <button
                                                className="w-1/2 h-full focus:outline-none focus:bg-black focus:bg-opacity-10"
                                                onClick={advanceStory}
                                                aria-label="Next Slide"
                                            />
                                        </div>

                                        {/* Mobile title bar */}
                                        <div className="absolute bottom-0 left-0 right-0 p-4 bg-royal-pine text-ivory">
                                            <div className="flex justify-between items-center">
                                                <h3 className="heading-font text-xl">{item.title}</h3>
                                                <button
                                                    onClick={advanceStory}
                                                    className="flex items-center space-x-2 text-sm hover:text-logo-color transition-colors"
                                                >
                                                    <span>NEXT</span>
                                                    <ArrowRightIcon className="w-4 h-4"/>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Mobile navigation dots */}
                            <div className="flex justify-center space-x-2 mt-6">
                                {items.map((_, index) => {
                                    const isActive = itemOrder[0] === index;
                                    return (
                                        <button
                                            key={index}
                                            onClick={() => {
                                                const newOrder = [index, ...itemOrder.filter(i => i !== index)];
                                                setItemOrder(newOrder);
                                                setActiveSlideIndex(0);
                                                setIsUserInteracting(true);
                                                setTimeout(() => setIsUserInteracting(false), 10000);
                                            }}
                                            className={`w-3 h-3 rounded-full transition-colors ${
                                                isActive ? 'bg-royal-pine' : 'bg-gray-300'
                                            }`}
                                            aria-label={`Switch to ${items[index].title}`}
                                        />
                                    );
                                })}
                            </div>
                        </div>

                        {/* Desktop Layout */}
                        <div className="hidden md:block relative h-full w-full">
                            {items.map((item, index) => {
                                const position = itemOrder.indexOf(index);
                                const isMain = position === 0;

                                return (
                                    <div
                                        key={item.title}
                                        className={`card-shuffler ${getCardClass(index)}`}
                                        onClick={() => {
                                            if (position === 1) {
                                                handleShuffleNext();
                                                setIsUserInteracting(true);
                                                setTimeout(() => setIsUserInteracting(false), 10000);
                                            }
                                            if (position === 2) {
                                                handleShufflePrev();
                                                setIsUserInteracting(true);
                                                setTimeout(() => setIsUserInteracting(false), 10000);
                                            }
                                        }}
                                    >
                                        {isMain ? (
                                            // Main Card
                                            <div className="relative w-full h-full">
                                                {item.images.map((image, imgIndex) => (
                                                    <Picture
                                                        key={`${item.title}-${imgIndex}`}
                                                        {...(image.toLowerCase().endsWith('.webp') ? { webpSrc: image } : image.toLowerCase().endsWith('.png') ? { pngSrc: image } : { jpgSrc: image })}
                                                        alt={`${item.title} slide ${imgIndex + 1}`}
                                                        loading={imgIndex === 0 ? "eager" : "lazy"}
                                                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                                                            imgIndex === activeSlideIndex ? 'opacity-100' : 'opacity-0'
                                                        }`}
                                                        onErrorSrc={`https://placehold.co/800x600/153D40/C6A375?text=${encodeURIComponent(item.title)}`}
                                                    />
                                                ))}

                                                {/* Overlay */}
                                                <div className="absolute inset-0 bg-black bg-opacity-40"/>

                                                {/* Progress Indicators */}
                                                <div className="absolute top-4 left-4 right-4">
                                                    <div className="flex space-x-2">
                                                        {item.images.map((_, imgIndex) => (
                                                            <div key={imgIndex}
                                                                 className="flex-1 h-1 bg-white bg-opacity-30 rounded-full overflow-hidden">
                                                                <div
                                                                    className={`h-full bg-white transition-all ${
                                                                        imgIndex === activeSlideIndex ? 'story-progress' : ''
                                                                    } ${imgIndex < activeSlideIndex ? 'w-full' : 'w-0'}`}
                                                                    style={{animationDuration: `${DURATION}ms`}}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Interactive Areas */}
                                                <div className="absolute inset-0 flex">
                                                    <button
                                                        className="w-1/2 h-full cursor-pointer focus:outline-none focus:bg-black focus:bg-opacity-10"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            previousStory();
                                                        }}
                                                        aria-label="Previous slide"
                                                    />
                                                    <button
                                                        className="w-1/2 h-full cursor-pointer focus:outline-none focus:bg-black focus:bg-opacity-10"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            advanceStory();
                                                        }}
                                                        aria-label="Next slide"
                                                    />
                                                </div>

                                                {/* Title Bar */}
                                                <div
                                                    className="absolute bottom-0 left-0 right-0 p-6 bg-royal-pine text-ivory flex justify-between items-center">
                                                    <div>
                                                        <h3 className="heading-font text-xl xl:text-2xl">{item.title}</h3>
                                                    </div>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            advanceStory();
                                                        }}
                                                        className="flex items-center space-x-2 border-b border-ivory pb-1 hover:text-logo-color transition-colors"
                                                    >
                                                        <span className="text-sm">NEXT</span>
                                                        <ArrowRightIcon className="w-4 h-4 xl:w-5 xl:h-5"/>
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            // Small Cards
                                            <div
                                                className="relative w-full h-full cursor-pointer group">
                                                <Picture
                                                    {...(item.thumbnail.toLowerCase().endsWith('.webp') ? { webpSrc: item.thumbnail } : item.thumbnail.toLowerCase().endsWith('.png') ? { pngSrc: item.thumbnail } : { jpgSrc: item.thumbnail })}
                                                    alt={item.title}
                                                    loading="lazy"
                                                    className="w-full h-full group-hover:scale-105 object-cover transition-transform duration-300"
                                                    onErrorSrc={`https://placehold.co/800x600/153D40/C6A375?text=${encodeURIComponent(item.title)}`}
                                                />
                                                <div className="absolute inset-0 bg-black bg-opacity-40"/>
                                                <div className="absolute top-0 left-0 right-0 px-4 py-2 bg-white">
                                                    <h3 className="heading-font text-sm xl:text-base text-royal-pine truncate">
                                                        {item.title}
                                                    </h3>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>
        );
    }
);

InteractiveGridCarousel.displayName = "InteractiveGridCarousel";